'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import NavMarker from 'react-rhumb/NavMarker';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Raven from 'Raven';
import { parse as parseParams } from 'hub-http/helpers/params';
import * as CustomPropTypes from '../../../constants/PropTypes';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import FormattedDuration from 'I18n/components/FormattedDuration';
import Loadable from 'UIComponents/decorators/Loadable';
import * as RequestState from 'ecosystem-marketplace-core/constants/RequestState';
import * as ReviewType from 'ecosystem-marketplace-core/constants/reviews/ReviewType';
import Review from 'ecosystem-marketplace-core/records/Review';
import { getDisplayedReviewerName } from 'ecosystem-marketplace-core/utils/i18n';
import { getUserEmail, getUserFirstName, getUserLastName } from '../../../selectors/auth';
import { submitReview as submitReviewRequest } from '../../../actions/ReviewsActions';
import { postNotification } from 'ReduxMessenger/actions/NotificationActions';
import AppReviewability from '../../../records/AppReviewability';
import { UNREVIEWABLE_ALERT_ID, UnreviewableReasons } from '../../../constants/Reviewability';
import { getReviewWriteRequestState } from '../../../selectors/requestStates';
const AsyncReviewWizard = Loadable({
  loader: () => import(
  /* webpackChunkName: 'review-wizard' */
  'ecosystem-marketplace-core/components/reviews/wizard/app/AppReviewWizard')
});
const textPath = 'appMarketplace.detail.reviews.unreviewable';

const ReviewWizard = ({
  appName,
  appIcon,
  firstName,
  lastName,
  offeringId,
  onClose,
  onSuccess,
  onTrackInteraction,
  postUnreviewableAlert,
  reviewability,
  reviewWriteStatus,
  slug,
  submitReview,
  supportLink,
  userEmail
}) => {
  const displayName = getDisplayedReviewerName({
    firstName,
    lastName
  });
  const {
    search
  } = useLocation();
  const {
    eco_review_source,
    rating
  } = parseParams(search.slice(1));
  const reviewSource = eco_review_source || 'details';
  const overallRating = /^[1-5]$/.test(rating) ? parseInt(rating, 10) : undefined;
  const defaultReview = useMemo(() => {
    const {
      portalEmployeeSize: employeeSize,
      portalIndustry: industry
    } = reviewability;
    const hasDefaultCompanyInfo = employeeSize || industry;
    const storedReview = Review.from(JSON.parse(sessionStorage.getItem(`${slug}_review`)));
    let displayedReview = storedReview;

    if (hasDefaultCompanyInfo) {
      displayedReview = displayedReview.setEmployees(employeeSize).setIndustry(industry);
    }

    if (overallRating) {
      displayedReview = displayedReview.setOverallRating(overallRating);
    }

    return displayedReview;
  }, [overallRating, reviewability, slug]);
  const handleSubmit = useCallback(review => {
    sessionStorage.removeItem(`${slug}_review`);
    submitReview(review.set('createdAt', new Date().valueOf()).set('reviewerDisplayName', displayName).set('offeringId', offeringId).set('reviewType', ReviewType.APP), slug).then(() => {
      onSuccess();
    }).catch(err => {
      Raven.captureMessage('Failed to leave a review', {
        level: 'error',
        extra: Object.assign({
          offeringId
        }, err)
      });
    });
    onTrackInteraction('submit review', 'review', undefined, {
      reviewSource
    });
  }, [displayName, offeringId, onSuccess, onTrackInteraction, slug, submitReview, reviewSource]);
  const updateSessionStorage = useCallback(updatedReview => {
    sessionStorage.setItem(`${slug}_review`, JSON.stringify(updatedReview));
  }, [slug]);
  const onWizardLoad = useCallback(() => {
    onTrackInteraction('open review wizard', 'review', undefined, {
      reviewSource
    });
  }, [onTrackInteraction, reviewSource]);
  const onStepChange = useCallback((prevStep, nextStep) => {
    onTrackInteraction('change review wizard step', 'review', nextStep, {
      prevStep,
      reviewSource
    });
  }, [onTrackInteraction, reviewSource]);
  const onReject = useCallback(() => {
    onTrackInteraction('dismiss review wizard', 'review', undefined, {
      reviewSource
    });
    onClose();
  }, [onTrackInteraction, onClose, reviewSource]);
  useEffect(() => {
    if (!reviewability.isReviewable()) {
      const reason = reviewability.getUnreviewableReason();

      let message = /*#__PURE__*/_jsx(FormattedMessage, {
        message: `${textPath}.${reason}`
      });

      if (reason === UnreviewableReasons.UNINSTALLED) {
        const uninstallWindow = /*#__PURE__*/_jsx(FormattedDuration, {
          from: 1,
          to: reviewability.get('maxMillisAfterUninstall')
        });

        message = /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: `${textPath}.${reason}`,
          options: {
            uninstallWindow
          }
        });
      }

      postUnreviewableAlert({
        id: UNREVIEWABLE_ALERT_ID,
        type: 'danger',
        titleText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `${textPath}.titleText`
        }),
        message
      });
    }
  }, [reviewability, postUnreviewableAlert]);
  return /*#__PURE__*/_jsx(NavMarker, {
    name: "REVIEW_WIZARD_LOAD",
    children: reviewability.isReviewable() && /*#__PURE__*/_jsx(AsyncReviewWizard, {
      reviewableName: appName,
      reviewableIconUrl: appIcon,
      failed: reviewWriteStatus === RequestState.FAILED,
      defaultReview: defaultReview,
      displayName: displayName,
      loading: reviewWriteStatus === RequestState.PENDING,
      onReject: onReject,
      onChange: updateSessionStorage,
      onConfirm: handleSubmit,
      onStepChange: onStepChange,
      onWizardLoad: onWizardLoad,
      supportLink: supportLink,
      userEmail: userEmail
    })
  });
};

ReviewWizard.propTypes = {
  appName: PropTypes.string.isRequired,
  appIcon: PropTypes.string.isRequired,
  firstName: PropTypes.string,
  lastName: PropTypes.string,
  offeringId: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  onTrackInteraction: PropTypes.func.isRequired,
  postUnreviewableAlert: PropTypes.func.isRequired,
  reviewability: PropTypes.instanceOf(AppReviewability).isRequired,
  reviewWriteStatus: CustomPropTypes.requestState.isRequired,
  slug: PropTypes.string.isRequired,
  submitReview: PropTypes.func.isRequired,
  supportLink: PropTypes.string,
  userEmail: PropTypes.string
};

const mapStateToProps = state => ({
  firstName: getUserFirstName(state),
  lastName: getUserLastName(state),
  reviewWriteStatus: getReviewWriteRequestState(state),
  userEmail: getUserEmail(state)
});

const mapDispatchToProps = {
  submitReview: submitReviewRequest,
  postUnreviewableAlert: postNotification
};
export default connect(mapStateToProps, mapDispatchToProps)(ReviewWizard);